import {
  Button,
  Carousel,
  FullPageLoading,
  Stack,
  Text,
  Title,
} from "@assetwatch/ui";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import type {
  MotionAmplificationJob,
  MotionAmplificationJobId,
} from "../../clients/motion-amplification";
import type { JobView } from "../motion-amplification/types";
import { JobCard } from "./JobCard";
import { JobMedia } from "./JobMedia";
import { statusLabel } from "./motionAmplificationJobState";

type ProcessedValue = number | string | null | undefined;

function valueOrDash(value: ProcessedValue, fallback = "—") {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value === "number") {
    return Number.isFinite(value) ? value.toFixed(0) : fallback;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) && value.trim() !== ""
    ? numericValue.toFixed(0)
    : value;
}

function formatDate(value: ProcessedValue) {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date.toLocaleString() : null;
}

function JobMetadata({
  view,
  onShowDetails,
  t,
}: {
  view: JobView;
  onShowDetails: (job: MotionAmplificationJob) => void;
  t: TFunction;
}) {
  const { job, uploadState } = view;
  const title =
    job.name ||
    job.fileName ||
    t("motionAmplification.jobFallback", { id: job.jobId });
  return (
    <>
      <Title size="md">{title}</Title>
      <Stack gap={4}>
        <Text tone="subtle">{t("motionAmplification.status")}</Text>
        <Text>{statusLabel(job, t, uploadState)}</Text>
      </Stack>
      <Stack gap={4}>
        <Text tone="subtle">{t("motionAmplification.uploaded")}</Text>
        <Text>{formatDate(job.dateCreated) || "—"}</Text>
      </Stack>
      <Stack gap={4}>
        <Text tone="subtle">{t("motionAmplification.frameRate")}</Text>
        <Text>
          {valueOrDash(
            job.parameters?.fps,
            t("motionAmplification.detectedFromVideo"),
          )}
          {job.parameters?.fps ? " FPS" : ""}
        </Text>
      </Stack>
      <Stack gap={4}>
        <Text tone="subtle">{t("motionAmplification.preset")}</Text>
        <Text>{valueOrDash(job.preset)}</Text>
      </Stack>
      {job.assignment && (
        <>
          <Stack gap={4}>
            <Text tone="subtle">{t("motionAmplification.customer")}</Text>
            <Text>{job.assignment.customerName}</Text>
          </Stack>
          <Stack gap={4}>
            <Text tone="subtle">{t("motionAmplification.facility")}</Text>
            <Text>{job.assignment.facilityName}</Text>
          </Stack>
          <Stack gap={4}>
            <Text tone="subtle">{t("motionAmplification.machine")}</Text>
            <Text>{job.assignment.machineName}</Text>
          </Stack>
        </>
      )}
      {job.description && (
        <Stack gap={4}>
          <Text tone="subtle">{t("motionAmplification.description")}</Text>
          <Text>{job.description}</Text>
        </Stack>
      )}
      <Button variant="ghost" onClick={() => onShowDetails(job)}>
        {t("motionAmplification.moreDetails")}
      </Button>
    </>
  );
}

export function MotionAmplificationGalleryAdapter({
  jobs,
  isLoading,
  selectedJobId,
  onSelectJob,
  onShowDetails,
  onAddVideo,
}: {
  jobs: readonly JobView[];
  isLoading: boolean;
  selectedJobId: MotionAmplificationJobId | null;
  onSelectJob: (jobId: MotionAmplificationJobId) => void;
  onShowDetails: (job: MotionAmplificationJob) => void;
  onAddVideo: () => void;
}) {
  const { t } = useTranslation();
  if (isLoading) {
    return (
      <FullPageLoading aria-label={t("motionAmplification.loadingJobs")} />
    );
  }

  if (jobs.length === 0) {
    return (
      <Stack align="center" gap={12}>
        <Text tone="subtle">{t("motionAmplification.noVideos")}</Text>
        <Button onClick={onAddVideo}>
          {t("motionAmplification.addVideo")}
        </Button>
      </Stack>
    );
  }

  const selectedView =
    jobs.find(({ job }) => job.jobId === selectedJobId) ?? jobs[0];
  return (
    <Stack gap={24}>
      <Carousel aria-label={t("motionAmplification.carouselVideos")}>
        {jobs.map((view) => (
          <JobCard
            key={view.job.jobId}
            view={view}
            selected={view.job.jobId === selectedView.job.jobId}
            onSelect={() => onSelectJob(view.job.jobId)}
            t={t}
          />
        ))}
      </Carousel>
      <div className="grid min-w-0 gap-(--spacing-24) lg:grid-cols-[minmax(0,2fr)_minmax(260px,1fr)]">
        <div className="min-w-0 rounded-(--radius-sm) bg-surface-container p-(--spacing-12)">
          <JobMedia view={selectedView} t={t} />
        </div>
        <Stack gap={16}>
          <JobMetadata
            view={selectedView}
            onShowDetails={onShowDetails}
            t={t}
          />
        </Stack>
      </div>
    </Stack>
  );
}
